import { useEffect } from "react";

interface SEOProps {
  title: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  ogUrl?: string;
  /** Optional article publish date for Twitter card label */
  datePublished?: string;
}

/**
 * Sets document title, meta description, canonical, Open Graph, and Twitter Card tags for SPA pages.
 * Title is appended with " | Hintsly" unless it already contains "Hintsly".
 */
export function useSEO({ title, description, canonical, ogImage, ogUrl, datePublished }: SEOProps) {
  useEffect(() => {
    const fullTitle = title.includes("Hintsly") ? title : `${title} | Hintsly`;
    document.title = fullTitle;

    if (description) {
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", description);
    }

    // Update canonical
    if (canonical) {
      const link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (link) link.href = canonical;
    }

    // Helper to upsert meta tags by property or name attribute
    const setMetaTag = (attr: "property" | "name", key: string, content: string) => {
      let tag = document.querySelector(`meta[${attr}="${key}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    // Open Graph tags
    setMetaTag("property", "og:title", fullTitle);
    setMetaTag("property", "og:type", "website");
    if (description) setMetaTag("property", "og:description", description);
    if (ogImage) setMetaTag("property", "og:image", ogImage);
    if (ogUrl || canonical) setMetaTag("property", "og:url", ogUrl || canonical || "");

    // Twitter Card tags
    setMetaTag("name", "twitter:card", "summary_large_image");
    setMetaTag("name", "twitter:site", "@hintslyau");
    setMetaTag("name", "twitter:title", fullTitle);
    if (description) setMetaTag("name", "twitter:description", description);
    if (ogImage) setMetaTag("name", "twitter:image", ogImage);
    if (datePublished) {
      setMetaTag("name", "twitter:label1", "Published");
      setMetaTag("name", "twitter:data1", datePublished);
    }

    return () => {
      document.title = "Hintsly — Give the Gift They'll Actually Love | Australian Gift Registry";
    };
  }, [title, description, canonical, ogImage, ogUrl, datePublished]);
}
